import React, { useEffect, useState, useCallback } from "react";
import { Map as MapIcon, List, SlidersHorizontal, LocateFixed, X } from "lucide-react";
import PlazaMap from "@/components/PlazaMap";
import StudyCard from "@/components/StudyCard";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

export default function Plaza() {
  const [studies, setStudies] = useState([]);
  const [books, setBooks] = useState([]);
  const [view, setView] = useState("split");
  const [loading, setLoading] = useState(true);
  const [userLoc, setUserLoc] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ day: "", book: "", time_of_day: "", min_size: "" });

  const load = useCallback(async (loc) => {
    setLoading(true);
    try {
      const params = {};
      if (loc) { params.lat = loc[0]; params.lng = loc[1]; }
      if (filters.day !== "") params.day = filters.day;
      if (filters.book) params.book = filters.book;
      if (filters.time_of_day) params.time_of_day = filters.time_of_day;
      if (filters.min_size) params.min_size = filters.min_size;
      const { data } = await api.get("/studies", { params });
      setStudies(data.studies);
    } catch { toast.error("Could not load studies"); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { api.get("/studies/books").then(({ data }) => setBooks(data.books)); }, []);
  useEffect(() => { load(userLoc); }, [load, userLoc]);

  const locate = () => {
    if (!navigator.geolocation) return toast.error("Geolocation not supported");
    navigator.geolocation.getCurrentPosition(
      (pos) => { setUserLoc([pos.coords.latitude, pos.coords.longitude]); toast.success("Centered on your location"); },
      () => toast.error("Location permission denied"),
    );
  };

  const clearFilters = () => setFilters({ day: "", book: "", time_of_day: "", min_size: "" });
  const activeCount = Object.values(filters).filter((v) => v !== "").length;
  const center = userLoc || (studies[0] ? [studies[0].venue.lat, studies[0].venue.lng] : null);

  const FilterBar = (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
      <Select value={filters.day} onValueChange={(v) => setFilters({ ...filters, day: v === "any" ? "" : v })}>
        <SelectTrigger data-testid="filter-day" className="rounded-full border-[#E8E4DF] bg-white text-sm"><SelectValue placeholder="Any day" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="any">Any day</SelectItem>
          {DAYS.map((d, i) => <SelectItem key={d} value={String(i)}>{d}</SelectItem>)}
        </SelectContent>
      </Select>
      <Select value={filters.time_of_day} onValueChange={(v) => setFilters({ ...filters, time_of_day: v === "any" ? "" : v })}>
        <SelectTrigger data-testid="filter-time" className="rounded-full border-[#E8E4DF] bg-white text-sm"><SelectValue placeholder="Any time" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="any">Any time</SelectItem>
          <SelectItem value="morning">Morning</SelectItem>
          <SelectItem value="afternoon">Afternoon</SelectItem>
          <SelectItem value="evening">Evening</SelectItem>
        </SelectContent>
      </Select>
      <Select value={filters.book} onValueChange={(v) => setFilters({ ...filters, book: v === "any" ? "" : v })}>
        <SelectTrigger data-testid="filter-book" className="rounded-full border-[#E8E4DF] bg-white text-sm"><SelectValue placeholder="Any book" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="any">Any book</SelectItem>
          {books.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
        </SelectContent>
      </Select>
      <Select value={filters.min_size} onValueChange={(v) => setFilters({ ...filters, min_size: v === "any" ? "" : v })}>
        <SelectTrigger data-testid="filter-size" className="rounded-full border-[#E8E4DF] bg-white text-sm"><SelectValue placeholder="Any size" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="any">Any size</SelectItem>
          <SelectItem value="3">Active (3+)</SelectItem>
          <SelectItem value="6">6+ members</SelectItem>
          <SelectItem value="10">10+ members</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <div className="mx-auto max-w-6xl px-4 py-5 sm:px-6">
      <div className="mb-4 flex items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-[#0F3D66] sm:text-3xl">The Plaza</h1>
          <p className="text-sm text-[#6B6763]">{loading ? "Finding studies…" : `${studies.length} studies meeting in public near you`}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={locate} data-testid="locate-btn" className="rounded-full border-[#E8E4DF] bg-white text-[#0F3D66]">
            <LocateFixed size={16} className="mr-1" /> Near me
          </Button>
          <div className="hidden overflow-hidden rounded-full border border-[#E8E4DF] bg-white sm:flex">
            {[["split", MapIcon], ["list", List]].map(([v, Icon]) => (
              <button key={v} onClick={() => setView(v)} data-testid={`view-${v}`}
                className={`px-3 py-2 ${view === v ? "bg-[#F36353] text-white" : "text-[#6B6763]"}`}>
                <Icon size={16} />
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="mb-4">
        <button onClick={() => setShowFilters(!showFilters)} data-testid="toggle-filters"
          className="mb-3 flex items-center gap-2 rounded-full border border-[#E8E4DF] bg-white px-4 py-2 text-sm font-medium text-[#0F3D66] sm:hidden">
          <SlidersHorizontal size={15} /> Filters {activeCount > 0 && <span className="rounded-full bg-[#F36353] px-1.5 text-xs text-white">{activeCount}</span>}
        </button>
        <div className={`${showFilters ? "block" : "hidden"} sm:block`}>{FilterBar}</div>
        {activeCount > 0 && (
          <button onClick={clearFilters} data-testid="clear-filters" className="mt-2 flex items-center gap-1 text-xs font-medium text-[#F36353]">
            <X size={12} /> Clear filters
          </button>
        )}
      </div>

      <div className={`grid gap-4 ${view === "split" ? "lg:grid-cols-12" : ""}`}>
        {view !== "list" && (
          <div className={`overflow-hidden rounded-2xl border border-[#E8E4DF] ${view === "split" ? "lg:col-span-7" : ""}`} style={{ height: view === "split" ? "72vh" : "50vh" }}>
            <PlazaMap studies={studies} center={center} userLoc={userLoc} />
          </div>
        )}
        <div className={`${view === "split" ? "lg:col-span-5" : ""} space-y-3 ${view === "split" ? "lg:max-h-[72vh] lg:overflow-y-auto lg:pr-1" : "sm:grid sm:grid-cols-2 sm:gap-3 sm:space-y-0 lg:grid-cols-3"}`}>
          {!loading && studies.length === 0 && (
            <div className="rounded-2xl border border-dashed border-[#E8E4DF] bg-white p-8 text-center text-sm text-[#6B6763]">
              No studies match these filters yet.
            </div>
          )}
          {studies.map((s, i) => <StudyCard key={s.id} study={s} index={i} />)}
        </div>
      </div>
    </div>
  );
}
